import React, { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clientAPI, reportsAPI } from '../../services/apiServices';
import { useFetch } from '../../hooks/useFetch';
import { useFeedback } from '../../contexts/FeedbackContext';
import { Alert, Spinner } from '../../components/common/UI';
import { getDerivedBusinessStatus } from '../../utils/businessStatus';
import {
  ArrowLeftIcon,
  BriefcaseIcon,
  BuildingIcon,
  EditIcon,
  GlobeIcon,
  MailIcon,
  MapPinIcon,
  PhoneIcon,
  TrashIcon,
  UserIcon,
} from '../../components/common/Icons';
import AuthenticatedImage from '../../components/common/AuthenticatedImage';

const STATUS_CLASSES = {
  pre_initial: 'bg-amber-50 text-amber-700',
  started: 'bg-amber-50 text-amber-700',
  in_progress: 'bg-emerald-50 text-emerald-700',
  paused: 'bg-rose-50 text-rose-700',
  aborted: 'bg-rose-50 text-rose-700',
  canceled: 'bg-rose-50 text-rose-700',
  completed: 'bg-sky-50 text-sky-700',
};

const TYPE_CLASSES = {
  business: 'bg-emerald-50 text-emerald-700',
  involved: 'bg-amber-50 text-amber-700',
  both: 'bg-sky-50 text-sky-700',
};

const fetchClientDetail = async (id) => {
  const [clientRes, progressRes] = await Promise.all([
    clientAPI.getOne(id),
    reportsAPI.progress(),
  ]);

  return {
    data: {
      client: clientRes.data?.data || clientRes.data,
      progress: progressRes.data?.data || progressRes.data || [],
    },
  };
};

const buildAddress = (client) => {
  const street = [client.street, client.number].filter(Boolean).join(', ');
  const city = [client.city, client.state].filter(Boolean).join(' - ');
  return [street, client.neighborhood, city, client.zipCode].filter(Boolean).join(', ');
};

const ClientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { confirm, notify } = useFeedback();
  const { data, loading, error } = useFetch(() => fetchClientDetail(id), [id]);

  const client = data?.client;
  const type = client?.clientType || 'business';

  const activeBusinesses = useMemo(() => {
    if (!client) return [];
    const progressByBusiness = new Map(
      (data?.progress || []).map((business) => [business.id, getDerivedBusinessStatus(business.progressList)])
    );

    return (client.businesses || [])
      .filter((business) => business.isActive !== false)
      .map((business) => ({
        ...business,
        derivedStatus: progressByBusiness.get(business.id) || getDerivedBusinessStatus(business.progressList),
      }));
  }, [client, data]);

  const handleDelete = async () => {
    const ok = await confirm({
      variant: 'danger',
      title: t('contacts.deleteTitle'),
      message: t('contacts.deleteDetailMessage'),
      confirmText: t('common.delete'),
    });
    if (!ok) return;
    try {
      await clientAPI.remove(id);
      navigate('/clients');
    } catch {
      notify({ variant: 'danger', title: t('contacts.deleteErrorTitle'), message: t('contacts.deleteError') });
    }
  };

  if (loading) return <Spinner size="lg" />;
  if (error) return <Alert variant="error">{error}</Alert>;
  if (!client) return null;

  const address = buildAddress(client);
  return (
    <div className="mx-auto max-w-[1104px]">
      <div className="mb-7 flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <Link
            to="/clients"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-600 transition hover:bg-slate-100"
            aria-label={t('common.back')}
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-sky-50 text-sky-500">
            {client.avatar ? (
              <AuthenticatedImage source={client.avatar} alt="" className="h-full w-full rounded-full object-cover" />
            ) : type === 'involved' ? (
              <UserIcon className="h-8 w-8" />
            ) : (
              <BuildingIcon className="h-8 w-8" />
            )}
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-3xl font-bold tracking-tight text-slate-900">{client.name}</h1>
            <p className="mt-1 text-base text-slate-600">{client.document || t('contacts.documentMissing')}</p>
          </div>
        </div>

        <div className="flex gap-2">
          <Link
            to={`/clients/${id}/edit`}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm transition hover:border-sky-200 hover:text-sky-600"
          >
            <EditIcon className="h-4 w-4" />
            {t('common.edit')}
          </Link>
          <button
            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-red-500 shadow-sm transition hover:border-red-200 hover:bg-red-50"
            onClick={handleDelete}
            type="button"
          >
            <TrashIcon className="h-4 w-4" />
            {t('common.delete')}
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_352px]">
        <div className="space-y-6">
          <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-soft">
            <h2 className="mb-5 text-lg font-bold text-slate-900">{t('contacts.contactInfo')}</h2>
            <div className="grid gap-5 md:grid-cols-2">
              <InfoItem icon={<MailIcon className="h-5 w-5" />} iconClass="bg-sky-50 text-sky-500" label={t('auth.email')} value={client.email || t('contacts.emailMissing')} />
              <InfoItem icon={<PhoneIcon className="h-5 w-5" />} iconClass="bg-sky-50 text-sky-500" label={t('profile.phone')} value={client.phone || t('contacts.phoneMissing')} />
              <InfoItem icon={<PhoneIcon className="h-5 w-5" />} iconClass="bg-emerald-50 text-emerald-600" label="WhatsApp" value={client.whatsapp || client.phone || t('contacts.whatsappMissing')} />
              <InfoItem icon={<GlobeIcon className="h-5 w-5" />} iconClass="bg-amber-50 text-amber-700" label={t('contacts.website')} value={client.website || t('contacts.websiteMissing')} />
            </div>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-soft">
            <h2 className="mb-5 text-lg font-bold text-slate-900">{t('contacts.address')}</h2>
            <InfoItem icon={<MapPinIcon className="h-5 w-5" />} iconClass="bg-slate-100 text-slate-700" value={address || t('contacts.addressMissing')} />
          </section>

          <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft">
            <div className="flex h-14 items-center justify-between border-b border-slate-200 px-4">
              <h2 className="font-bold text-slate-900">{t('contacts.activeBusinesses')}</h2>
              <span className="text-sm text-slate-700">
                {t('contacts.businessCount', { count: activeBusinesses.length })}
              </span>
            </div>
            {activeBusinesses.length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-500">{t('contacts.noActiveBusinesses')}</p>
            ) : (
              <div className="divide-y divide-slate-200">
                {activeBusinesses.map((business) => {
                  const status = business.derivedStatus;
                  return (
                    <Link key={business.id} to={`/businesses/${business.id}`} className="flex min-h-[72px] items-center gap-4 px-4 py-3 transition hover:bg-slate-50">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-500">
                        <BriefcaseIcon className="h-5 w-5" />
                      </div>
                      <span className="min-w-0 flex-1 truncate font-bold text-slate-900">{business.name}</span>
                      {status && (
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_CLASSES[status] || 'bg-slate-100 text-slate-600'}`}>
                          {t(`status.${status}`, status)}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <aside className="space-y-4">
          <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-soft">
            <h2 className="mb-5 text-lg font-bold text-slate-900">{t('contacts.details')}</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-600">{t('contacts.contactType')}</p>
                <span className={`mt-2 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${TYPE_CLASSES[type] || TYPE_CLASSES.business}`}>
                  {t(`contactTypes.${type}`, type)}
                </span>
              </div>
              <div>
                <p className="text-sm text-slate-600">{t('contacts.activityField')}</p>
                <p className="mt-1 font-bold text-slate-900">{client.activityField || t('common.notProvided')}</p>
              </div>
            </div>
          </section>

          {(client.email || client.whatsapp) && (
            <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-soft">
              <h2 className="mb-5 text-lg font-bold text-slate-900">{t('contacts.quickActions')}</h2>
              <div className="space-y-2">
                {client.email && (
                  <a
                    href={`mailto:${client.email}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-10 w-full items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-slate-900 hover:border-sky-200 hover:text-sky-600"
                  >
                    <MailIcon className="h-4 w-4" />
                    {t('contacts.sendEmail')}
                  </a>
                )}
                {client.whatsapp && (
                  <a
                    href={`https://wa.me/${client.whatsapp.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-10 w-full items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-slate-900 hover:border-emerald-200 hover:text-emerald-600"
                  >
                    <PhoneIcon className="h-4 w-4" />
                    WhatsApp
                  </a>
                )}
              </div>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
};

const InfoItem = ({ icon, iconClass, label, value }) => (
  <div className="flex items-center gap-3">
    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconClass}`}>
      {icon}
    </div>
    <div className="min-w-0">
      {label && <p className="text-xs text-slate-500">{label}</p>}
      <p className="truncate font-bold text-slate-900">{value}</p>
    </div>
  </div>
);

export default ClientDetailPage;
